import type { TeammateId } from "@repo/app-types/entities/teammate";
import type {
  CustomerInteractionNextAction,
  GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { AlertDialogVariant } from "@repo/ui/Modal/alertDialog";
import { ConfirmationModal } from "@repo/ui/Modal/confirmationModal";
import { IconPlayerPause } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { useCancelTeammateAgentCallTaskMutation } from "../../api/entities/teammateAgent.js";
import { useAppDispatch } from "../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";
import {
  formatCallActivityTimestamp,
  nextActionLabel,
} from "./callActivityColumns.js";

export interface GradingScheduledCallBannerProps {
  nextAction: CustomerInteractionNextAction;
  /** The interaction the call belongs to, so its cache entry can be refreshed. */
  gradingId: GradingId;
  /** The agent that will place the call — the cancel is scoped to it. */
  teammateAgentId: TeammateId;
  /** Who the call is with, for the banner copy. */
  customerName: string | undefined;
  timezone: string;
}

/**
 * The upcoming call on an interaction, and the one place it can be called off.
 * Shown above the assessment, which is empty until the call actually happens.
 */
export const GradingScheduledCallBanner: FC<GradingScheduledCallBannerProps> = (
  props,
) => {
  const { nextAction, gradingId, teammateAgentId, customerName, timezone } =
    props;

  const dispatch = useAppDispatch();
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const [cancelCallTask, { isLoading: isCanceling }] =
    useCancelTeammateAgentCallTaskMutation();

  const isPaused = nextAction.type === "paused";
  const scheduledAt = formatCallActivityTimestamp(
    nextAction.scheduled_at,
    timezone,
  );

  const handleCancel = async () => {
    // Ids are unique per notification: `showNotification` prepends without
    // deduping, so a repeat with the same id both duplicates a React key and
    // lets the older toast's dismissal timer take the newer one with it.
    const notificationId = `cancel-call-task-${nextAction.call_task_id}-${Date.now()}`;

    try {
      const result = await cancelCallTask({
        teammateAgentId,
        callTaskId: nextAction.call_task_id,
        gradingId,
      }).unwrap();

      showNotification(dispatch, {
        id: notificationId,
        primaryMessage: resolveCancelMessage(result),
        type: NotificationType.SUCCESS,
      });
    } catch {
      showNotification(dispatch, {
        id: notificationId,
        primaryMessage: "Failed to cancel the scheduled call",
        type: NotificationType.FAILURE,
      });
    }
  };

  return (
    <>
      <InfoBanner
        icon={
          isPaused ? (
            <IconPlayerPause aria-hidden className="size-5 text-blue-400" />
          ) : undefined
        }
        title={`${nextActionLabel[nextAction.type]} • Attempt ${nextAction.attempt_number}`}
        description={
          isPaused ? (
            <>
              Outbound calling is off for this agent, so the call planned for{" "}
              <time dateTime={nextAction.scheduled_at}>{scheduledAt}</time> will
              not be placed. Re-enable the agent by{" "}
              <time dateTime={nextAction.expires_at}>
                {formatCallActivityTimestamp(nextAction.expires_at, timezone)}
              </time>{" "}
              to keep it.
            </>
          ) : (
            <>
              A call with {customerName || "this customer"} is scheduled for{" "}
              <time dateTime={nextAction.scheduled_at}>{scheduledAt}</time>.
            </>
          )
        }
        actions={
          nextAction.can_cancel ? (
            <Button
              size={Size.SMALL}
              variant={ButtonVariant.Warning}
              isDisabled={isCanceling}
              onClick={() => setIsConfirmingCancel(true)}
            >
              Cancel call
            </Button>
          ) : undefined
        }
      />

      <ConfirmationModal
        isOpen={isConfirmingCancel}
        onOpenChange={setIsConfirmingCancel}
        title="Cancel scheduled call?"
        confirmText="Cancel call"
        cancelText="Keep scheduled"
        variant={AlertDialogVariant.Warning}
        onConfirm={() => void handleCancel()}
      >
        Canceling prevents the call from being placed and removes it from
        Scheduled.
      </ConfirmationModal>
    </>
  );
};

/**
 * The endpoint reports three outcomes, not two. A call already being placed is
 * not torn down, and a task that had already finished was never cancelable —
 * claiming either as "canceled" would describe something that didn't happen.
 */
function resolveCancelMessage(result: {
  canceled: boolean;
  cancelRequested: boolean;
}): string {
  if (result.canceled) {
    return "Scheduled call canceled";
  }

  return result.cancelRequested
    ? "Call canceled for every attempt after the one in progress"
    : "This call had already finished, so there was nothing to cancel";
}
